<script>
  import { _ } from 'svelte-i18n';
  import { onMount, onDestroy, createEventDispatcher } from 'svelte';

  const dispatch = createEventDispatcher();

  // Driven by the invitation's antiCheatEnabled flag. When off, the webcam
  // check is dropped entirely — the camera is never requested, so the browser
  // does not prompt for a permission the assessment will not use.
  export let webcamEnabled = true;

  // Microphone variables
  let audioContext;
  let analyser;
  let dataArray;
  let animationFrame;
  let volume = 0;
  let maxVolume = 0;

  // Webcam variables
  let videoElement;
  let canvasElement;
  let stream;
  let aborter;
  let webcamError = null;
  let capturedImage = null;

  // Speaker test variables
  let audioTestPlayed = false;
  let speakerConfirmed = false;
  let imageConfirmed = false;

  $: isReady =
    maxVolume >= 0.05 &&
    speakerConfirmed &&
    imageConfirmed &&
    (!webcamEnabled || !!(stream && !webcamError && capturedImage));
  $: if (isReady) dispatch('deviceReady', { ready: true });

  onMount(async () => {
    aborter = new AbortController();

    // Check microphone
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (aborter?.signal.aborted) {
        audioStream.getTracks().forEach(track => track.stop());
      } else {
        audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(audioStream);
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;

        const bufferLength = analyser.frequencyBinCount;
        dataArray = new Uint8Array(bufferLength);
        source.connect(analyser);

        const update = () => {
          analyser.getByteFrequencyData(dataArray);
          const avg = dataArray.reduce((a, b) => a + b) / dataArray.length;
          volume = avg / 256;
          maxVolume = Math.max(maxVolume, volume);
          animationFrame = requestAnimationFrame(update);
        };
        update();
      }
    } catch (err) {
      console.error('Microphone access denied or error:', err);
    }

    // Check webcam
    if (!webcamEnabled) return;

    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true });
      if (aborter?.signal.aborted) {
        s.getTracks().forEach(track => track.stop());
      } else {
        stream = s;
        if (videoElement) {
          videoElement.srcObject = stream;
        }
      }
    } catch (err) {
      console.error('Webcam access denied or error:', err);
      webcamError = err.message || 'Webcam access denied';
    }
  });

  onDestroy(() => {
    aborter?.abort();
    if (animationFrame) cancelAnimationFrame(animationFrame);
    if (audioContext && audioContext.state !== 'closed') audioContext.close();
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  });

  function takePhoto() {
    if (!videoElement || !canvasElement) return;

    const context = canvasElement.getContext('2d');
    canvasElement.width = videoElement.videoWidth;
    canvasElement.height = videoElement.videoHeight;
    context.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);

    capturedImage = canvasElement.toDataURL('image/png');
    dispatch('photoCaptured', { imageData: capturedImage });
  }

  function playTestSound() {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800 Hz beep
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime); // Low volume

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5); // 0.5 second beep

    audioTestPlayed = true;
  }

  function confirmSpeaker() {
    speakerConfirmed = true;
  }

  function confirmImageRender() {
    imageConfirmed = true;
  }
</script>

<style>
  .device-checker {
    width: 100%;
    margin: 20px 0;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
  }

  .device-checker.no-webcam {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .section {
    margin-bottom: 10px;
    background-color: #f7fbff;
    padding: 1.5rem;
    border-radius: 12px;
    border: 1px solid #dceaf3;
    display: flex;
    flex-direction: column;
    height: auto;
    min-height: fit-content;
    transition: background-color 0.2s ease, border-color 0.2s ease;
  }

  .section.passed {
    background-color: #ecfdf5;
    border-color: #86efac;
  }

  @media (max-width: 768px) {
    .device-checker {
      grid-template-columns: 1fr;
    }
  }

  .section h5 {
    margin-bottom: 10px;
    font-weight: 600;
  }

  .bar-container {
    width: 100%;
    height: 20px;
    background: #ddd;
    border-radius: 10px;
    overflow: hidden;
    margin-top: 10px;
    position: relative;
  }

  .bar {
    height: 100%;
    background-color: green;
    transition: width 0.05s linear;
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
  }

  .bar-max {
    height: 100%;
    background-color: rgba(0, 128, 0, 0.3);
    transition: width 0.2s linear;
    position: absolute;
    left: 0;
    top: 0;
    z-index: 0;
  }

  .video-container {
    width: 100%;
    aspect-ratio: 4 / 3;
    margin: 10px auto;
    border: 1px solid #ccc;
    border-radius: 8px;
    overflow: hidden;
    background: #e5e7eb;
  }

  video,
  .captured-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .photo-section {
    text-align: center;
    margin-top: 10px;
  }

  .captured-image {
    margin: 0;
    border: 0;
    border-radius: 0;
  }

  button {
    margin: 5px;
    padding: 8px 16px;
    background-color: #00a094;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }

  button:hover {
    background-color: #007a6d;
  }

  button:disabled {
    background-color: #cbd5e1;
    color: #475569;
    cursor: not-allowed;
    opacity: 1;
  }

  button:disabled:hover {
    background-color: #cbd5e1;
  }

  .error {
    color: #d32f2f;
    font-weight: 700;
    font-size: 0.9rem;
  }

  .success {
    color: #00a35b;
    font-weight: 700;
    font-size: 0.9rem;
  }

  .status-message {
    margin-top: auto;
    min-height: 3.5rem;
    display: flex;
    align-items: flex-end;
  }

  .status-message p {
    margin: 0;
  }

  .webcam-note {
    font-size: 0.95rem;
    color: #555;
    margin: 0.5rem 0 1rem 0;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.5;
  }

  .webcam-note-link {
    color: #00a896;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .image-preview {
    width: 100%;
    aspect-ratio: 4 / 3;
    margin: 10px auto;
    border: 1px solid #ccc;
    border-radius: 8px;
    overflow: hidden;
    background: #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .image-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
</style>

<div class="device-checker" class:no-webcam={!webcamEnabled}>
<div class="section" class:passed={maxVolume >= 0.05}>
  <h5>{maxVolume >= 0.05 ? `✅ ${$_("DEVICE_CHECKER_MICROPHONE_TEST")}` : $_("DEVICE_CHECKER_MICROPHONE_TEST")}</h5>
  <p style="font-size: 0.95rem; color: #555; margin: 0.5rem 0 1rem 0;">{$_("DEVICE_CHECKER_MICROPHONE_NOTE")}</p>
    <div class="bar-container">
      <div class="bar-max" style="width: {maxVolume * 100}%"></div>
      <div class="bar" style="width: {volume * 100}%"></div>
    </div>
    <div class="status-message">
      {#if maxVolume < 0.05}
        <p class="error">{$_("DEVICE_CHECKER_MICROPHONE_TOO_QUIET")}</p>
      {:else}
        <p class="success">{$_("DEVICE_CHECKER_MICROPHONE_WORKING")}</p>
      {/if}
    </div>
  </div>

  <div class="section" class:passed={speakerConfirmed}>
    <h5>{speakerConfirmed ? `✅ ${$_("DEVICE_CHECKER_SPEAKER_TEST")}` : $_("DEVICE_CHECKER_SPEAKER_TEST")}</h5>
    <p style="font-size: 0.95rem; color: #555; margin: 0.5rem 0 1rem 0;">{$_("DEVICE_CHECKER_SPEAKER_NOTE")}</p>
    <button on:click={playTestSound} disabled={speakerConfirmed}>
      <img src="https://www.svgrepo.com/show/500241/media-playback-start.svg" alt="" style="width: 16px; height: 16px; margin-right: 8px; vertical-align: middle; filter: brightness(0) invert(1);" />
      {$_("DEVICE_CHECKER_PLAY_SOUND")}
    </button>
    {#if audioTestPlayed}
      <button on:click={confirmSpeaker} disabled={speakerConfirmed}>{$_("DEVICE_CHECKER_I_HEARD_IT")}</button>
    {/if}
    <div class="status-message">
      {#if speakerConfirmed}
        <p class="success">{$_("DEVICE_CHECKER_SPEAKER_WORKING")}</p>
      {:else if audioTestPlayed}
        <p class="error">{$_("DEVICE_CHECKER_SPEAKER_CONFIRM_PROMPT")}</p>
      {/if}
    </div>
  </div>

  <div class="section" class:passed={imageConfirmed}>
    <h5>{imageConfirmed ? `✅ ${$_("DEVICE_CHECKER_IMAGE_RENDER_TEST")}` : $_("DEVICE_CHECKER_IMAGE_RENDER_TEST")}</h5>
    <p class="webcam-note">
      {$_("DEVICE_CHECKER_IMAGE_RENDER_NOTE_PREFIX")}
      <a class="webcam-note-link" href="https://www.speedtest.net" target="_blank" rel="noopener noreferrer">{$_("DEVICE_CHECKER_IMAGE_RENDER_NOTE_LINK")}</a>{$_("DEVICE_CHECKER_IMAGE_RENDER_NOTE_SUFFIX")}
    </p>
    <div class="image-preview">
      <img src="assets/img/red-square-test.png" alt="Red square render test" />
    </div>
    <div class="photo-section">
      <button on:click={confirmImageRender} disabled={imageConfirmed}>{$_("DEVICE_CHECKER_I_SEE_RED_RECTANGLE")}</button>
    </div>
    <div class="status-message">
      {#if imageConfirmed}
        <p class="success">{$_("DEVICE_CHECKER_IMAGE_RENDERING_WORKING")}</p>
      {/if}
    </div>
  </div>

  {#if webcamEnabled}
  <div class="section" class:passed={capturedImage && !webcamError}>
    <h5>{capturedImage && !webcamError ? `✅ ${$_("DEVICE_CHECKER_WEBCAM_TEST")}` : $_("DEVICE_CHECKER_WEBCAM_TEST")}</h5>
    <p class="webcam-note">{$_("DEVICE_CHECKER_WEBCAM_NOTE_PREFIX")}<a class="webcam-note-link" href="https://webcammictest.com" target="_blank" rel="noopener noreferrer">{$_("DEVICE_CHECKER_WEBCAM_NOTE_LINK")}</a>{$_("DEVICE_CHECKER_WEBCAM_NOTE_SUFFIX")}</p>
    <div class="video-container">
      {#if capturedImage}
        <img src={capturedImage} alt="Webcam preview" class="captured-image" />
      {:else}
        <video bind:this={videoElement} autoplay muted playsinline></video>
      {/if}
    </div>
    <div class="photo-section">
      <button on:click={takePhoto} disabled={!stream || webcamError || capturedImage}>{$_("DEVICE_CHECKER_I_CAN_SEE_MYSELF")}</button>
    </div>
    <div class="status-message">
      {#if webcamError}
        <p class="error">{webcamError}</p>
      {:else if capturedImage}
        <p class="success">{$_("DEVICE_CHECKER_WEBCAM_WORKING")}</p>
      {/if}
    </div>
  </div>
  {/if}
</div>

<canvas bind:this={canvasElement} style="display: none;"></canvas>